import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { ShieldIcon, CCTVCamera, DomeCCTV } from '../components/Illustrations';
import './About.css';

const About = () => {
  const { t } = useTranslation();
  const refs = useRef([]);

  const values = [
    { icon: '🤝', title: t('about.values.trust.title'), desc: t('about.values.trust.desc') },
    { icon: '⚡', title: t('about.values.quick.title'), desc: t('about.values.quick.desc') },
    { icon: '🏆', title: t('about.values.quality.title'), desc: t('about.values.quality.desc') },
    { icon: '🌍', title: t('about.values.local.title'), desc: t('about.values.local.desc') },
  ];

  const milestones = [
    { year: '2014', title: t('about.timeline.milestones.2014.title'), desc: t('about.timeline.milestones.2014.desc') },
    { year: '2017', title: t('about.timeline.milestones.2017.title'), desc: t('about.timeline.milestones.2017.desc') },
    { year: '2022', title: t('about.timeline.milestones.2022.title'), desc: t('about.timeline.milestones.2022.desc') },
    { year: '2023', title: t('about.timeline.milestones.2023.title'), desc: t('about.timeline.milestones.2023.desc') },
    { year: '2025', title: t('about.timeline.milestones.2025.title'), desc: t('about.timeline.milestones.2025.desc') },
  ];

  const team = [
    { name: 'Prashant/Shital Patil', role: t('about.team.members.proprietor.role'), desc: t('about.team.members.proprietor.desc') },
    { name: 'Prashant Patil', role: t('about.team.members.technical.role'), desc: t('about.team.members.technical.desc') },
    { name: 'Aniket Patil', role: t('about.team.members.gem.role'), desc: t('about.team.members.gem.desc') },
    { name: 'Bhagyashri Patil', role: t('about.team.members.marketing.role'), desc: t('about.team.members.marketing.desc') },
    { name: 'Dipak Patil', role: t('about.team.members.support.role'), desc: t('about.team.members.support.desc') },
  ];
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.08 }
    );
    refs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const add = el => { if (el && !refs.current.includes(el)) refs.current.push(el); };

  return (
    <div>
      {/* PAGE HERO */}
      <section className="page-hero">
        <div className="container page-hero__inner">
          <div className="reveal" ref={add}>
            <div className="section-badge">{t('about.hero.badge')}</div>
            <div className="vocal-local-badge">
              <span>🇮🇳</span>
              <span className="vocal-text">{t('home.hero.vocalForLocal') || 'Vocal for Local'}</span>
              <span className="vocal-divider">|</span>
              <span className="vocal-sub">{t('home.hero.kolhapursOwn') || "Kolhapur's Own"}</span>
            </div>
            <h1>{t('about.hero.title1')}<br /><em style={{fontStyle:'italic',color:'var(--green)'}}>{t('about.hero.title2')}</em></h1>
            <p className="page-hero__sub">
              {t('about.hero.subtitle')}
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '2rem' }}>
              <Link to="/order" className="btn btn-green">{t('about.hero.getInTouch')}</Link>
              <Link to="/services" className="btn btn-outline">{t('about.hero.ourServices')}</Link>
            </div>
          </div>
          <div className="reveal-right page-hero__icons" ref={add}>
            <CCTVCamera size={140} />
            <DomeCCTV size={100} />
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="container about-story">
          <div className="reveal-left" ref={add}>
            <div className="about-story__card">
              <div className="about-story__stat"><span>10+</span>{t('about.story.yearsExp')}</div>
              <div className="about-story__stat"><span>500+</span>{t('about.story.installations')}</div>
              <div className="about-story__stat"><span>100+</span>{t('about.story.clients')}</div>
              <div className="about-story__stat"><span>24/7</span>{t('about.story.support')}</div>
            </div>
            <div className="about-gem-badge">
              <ShieldIcon size={40} color="#f97316" />
              <div>
                <strong>{t('about.story.gemRegistered')}</strong>
                <p>{t('about.story.govtApproved')}</p>
              </div>
            </div>
          </div>
          <div className="reveal-right" ref={add}>
            <div className="section-badge">{t('about.story.badge')}</div>
            <h2>{t('about.story.title')}</h2>
            <p>{t('about.story.para1')}</p>
            <p>{t('about.story.para2')}</p>
            <p>{t('about.story.para3')}</p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-padding" style={{ background: 'var(--blue-pale)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge green">{t('about.values.badge')}</div>
            <h2>{t('about.values.title')}</h2>
          </div>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={v.title} className="value-card reveal" ref={add} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="value-icon">{v.icon}</div>
                <h3 className="value-title">{v.title}</h3>
                <p className="value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '4rem' }}>
            <div className="section-badge">{t('about.timeline.badge')}</div>
            <h2>{t('about.timeline.title')}</h2>
          </div>
          <div className="timeline">
            {milestones.map((m, i) => (
              <div key={m.year} className={`timeline-item ${i % 2 === 0 ? 'timeline-item--left' : 'timeline-item--right'} reveal`} ref={add} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="timeline-card">
                  <div className="timeline-year">{m.year}</div>
                  <h4 className="timeline-title">{m.title}</h4>
                  <p className="timeline-desc">{m.desc}</p>
                </div>
                <div className="timeline-dot" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section-padding" style={{ background: 'var(--blue-pale)', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge">{t('about.team.badge')}</div>
            <h2>{t('about.team.title')}</h2>
          </div>
          <div className="team-grid">
            {team.map((m, i) => (
              <div key={m.name} className="team-card reveal" ref={add} style={{ transitionDelay: `${i * 0.12}s` }}>
                <div className="team-avatar">{m.name.split(' ').map(w => w[0]).join('').slice(0, 2)}</div>
                <h4 className="team-name">{m.name}</h4>
                <div className="team-role">{m.role}</div>
                <p className="team-desc">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--navy)', padding: '5rem 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'white', marginBottom: '0.8rem', fontFamily: 'Fraunces', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>{t('about.cta.title')}</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }}>{t('about.cta.subtitle')}</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/order" className="btn btn-green">{t('about.cta.button1')}</Link>
            <Link to="/gem" className="btn btn-saffron">{t('about.cta.button2')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
